/* Shared text entrances; transactional UI and mechanical scene visibility keep their owners. */
(() => {
  'use strict';
  if (window.TMContentReveal) return;
  const selector = '[data-tm-reveal]';
  const textSelector = 'h1,h2,h3,h4,h5,h6,p,li,blockquote,figcaption';
  const exemptions = [
    '[data-tm-text-exempt]', '.sr-only', '[hidden]', 'nav', 'dialog', 'form', 'button',
    'label', 'details', 'table', 'dl', '[role="status"]', '[role="alert"]', '[aria-live]',
    '.site-header', '.tm-header', '.product-top', '.purchase-dock', '.product-subnav',
    '.catalog-card', '.product-card', '.je-product-card', '[data-policy-body]',
    '[data-price]', '.price', '.product-price', '.money', '.catalog-price', '.inventory', '.availability',
    '.shopify-policy__body', '.number', '.sound-time', '.audio-feedback',
    '.viewer-hint', '.form-status', '.fine-print', '.cookie-consent'
  ].join(',');
  const pending = new Set(), playing = new Map(), splits = new Map(), preparing = new Map();
  const seen = new WeakSet(), timers = new Set();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection;
  const navigation = performance.getEntriesByType('navigation')[0]?.type;
  const fresh = navigation !== 'back_forward' && !location.hash && scrollY < 20;
  let initial = true, interacted = false, destroyed = false;
  let observer, bodyObserver, prepareObserver, resizeFrame, jumpFrame;
  let lastScrollY = scrollY, lastScrollTime = performance.now(), lastWidth = innerWidth;

  function milliseconds(value, fallback) {
    const match = String(value).trim().match(/^(\d*\.?\d+)(ms|s)?$/i);
    if (!match) return fallback;
    const amount = Number(match[1]) * (match[2]?.toLowerCase() === 's' ? 1000 : 1);
    return Number.isFinite(amount) ? amount : fallback;
  }
  function enabled() {
    return !destroyed && !reduced.matches && !connection?.saveData &&
      !window.Shopify?.designMode && 'IntersectionObserver' in window &&
      typeof Element.prototype.animate === 'function' &&
      getComputedStyle(document.documentElement).getPropertyValue('--tm-reveal-ready').trim() === '1';
  }
  function textKind(el) {
    if (!el.matches(textSelector) || !el.textContent.trim() || el.closest(exemptions)) return null;
    if (el.matches('li,blockquote,figcaption') && el.querySelector('p,h1,h2,h3,h4,h5,h6,li')) return null;
    return /^H[1-6]$/.test(el.tagName) ? 'title' : 'body';
  }
  function manual(el) {
    const chapter = el.closest('.engineering-copy'), story = chapter?.closest('[data-landing-story]');
    return Boolean(story && story.dataset.motionState !== 'static' && story.dataset.motionLoading !== 'unavailable');
  }
  function discover(root) {
    const all = sel => [...(root.matches?.(sel) ? [root] : []), ...root.querySelectorAll(sel)];
    for (const el of all(selector)) {
      if (el.dataset.tmReveal !== 'media' && el.closest(exemptions)) el.removeAttribute('data-tm-reveal');
    }
    // Give headings and paragraphs their own entrance instead of moving a large parent twice.
    for (const el of all('[data-tm-reveal="copy"],[data-tm-reveal="media"]')) {
      if (el.matches(textSelector)) continue;
      const text = [...el.querySelectorAll(textSelector)].filter(textKind);
      if (!text.length) continue;
      const wasMedia = el.dataset.tmReveal === 'media';
      el.removeAttribute('data-tm-reveal');
      el.setAttribute('data-tm-reveal-group', '');
      if (wasMedia) for (const image of el.querySelectorAll('img,video')) {
        const frame = image.closest('picture,figure');
        const target = frame && !frame.querySelector(textSelector) ? frame : image;
        if (!target.closest('[data-tm-reveal="media"]')) target.dataset.tmReveal = 'media';
      }
    }
    for (const el of all(textSelector)) {
      const kind = textKind(el);
      if (kind) el.dataset.tmReveal = kind;
    }
    return all(selector);
  }
  function unobserve(el) {
    observer?.unobserve(el); bodyObserver?.unobserve(el); prepareObserver?.unobserve(el);
  }
  function revertSplit(el) {
    const split = splits.get(el);
    splits.delete(el);
    split?.revert();
  }
  function settle(el) {
    pending.delete(el); unobserve(el);
    preparing.delete(el);
    const animations = playing.get(el);
    playing.delete(el);
    for (const animation of animations || []) { animation.onfinish = null; animation.cancel(); }
    revertSplit(el);
    el.removeAttribute('data-tm-reveal-state');
    seen.add(el);
  }
  function settleAll() {
    new Set([...pending, ...playing.keys(), ...preparing.keys(), ...splits.keys()]).forEach(settle);
  }
  function fontsReady() {
    if (!document.fonts || document.fonts.status === 'loaded') return Promise.resolve();
    return new Promise(resolve => {
      const timer = setTimeout(() => { timers.delete(timer); resolve(); }, 1200);
      timers.add(timer);
      document.fonts.ready.then(() => { clearTimeout(timer); timers.delete(timer); resolve(); });
    });
  }
  function prepareBody(el) {
    if (splits.has(el)) return Promise.resolve(splits.get(el).lines);
    if (preparing.has(el)) return preparing.get(el).promise;
    const job = { width: innerWidth };
    job.promise = fontsReady().then(() => {
      if (preparing.get(el) !== job || !enabled() || seen.has(el) || !el.isConnected || job.width !== innerWidth) return null;
      // Keep live link/control nodes intact; their paragraph uses the same entrance as one group.
      if (!window.SplitText || document.fonts?.status === 'loading' || el.querySelector('a,button,input,select,textarea,svg,img,video')) return [el];
      window.gsap?.registerPlugin?.(window.SplitText);
      window.SplitText.register?.(window.gsap);
      const split = new window.SplitText(el, { type: 'lines', linesClass: 'body-line tm-body-line', aria: 'none' });
      splits.set(el, split);
      return split.lines.length ? split.lines : [el];
    }).catch(() => [el]).finally(() => { if (preparing.get(el) === job) preparing.delete(el); });
    preparing.set(el, job);
    return job.promise;
  }
  function start(el, rect, delay, forced, targets = [el]) {
    if (seen.has(el)) return;
    if (!enabled() || !el.isConnected || rect.bottom <= 0 ||
        (!forced && rect.top < innerHeight * .2) || el.contains(document.activeElement) ||
        el.parentElement?.closest('[aria-hidden="true"]')) { settle(el); return; }
    pending.delete(el); unobserve(el);
    const style = getComputedStyle(el);
    const token = (name, fallback) => style.getPropertyValue('--tm-reveal-' + name).trim() || fallback;
    const body = el.dataset.tmReveal === 'body';
    const distance = parseFloat(token(body ? 'body-distance' : 'distance', '')) || (body ? 26 : 24);
    const duration = milliseconds(token(body ? 'body-duration' : el.dataset.tmReveal === 'media' ? 'media-duration' : 'copy-duration', ''), body ? 700 : 720);
    const stagger = body ? milliseconds(token('body-stagger', ''), 160) : 0;
    const fadeDuration = body ? duration : milliseconds(token('fade-duration', ''), duration);
    const moveEase = token(body ? 'body-ease' : 'move-ease', 'cubic-bezier(.22,1,.36,1)');
    const fadeEase = body ? moveEase : token('fade-ease', moveEase);
    el.removeAttribute('data-tm-reveal-state');
    const animations = [];
    playing.set(el, animations);
    try {
      targets.forEach((target, index) => {
        const wait = delay + index * stagger;
        animations.push(target.animate([{opacity:0},{opacity:1}], {duration:fadeDuration,delay:wait,easing:fadeEase,fill:'backwards'}));
        animations.push(target.animate([{transform:'translateY(' + distance + 'px)'},{transform:'translateY(0)'}], {duration,delay:wait,easing:moveEase,fill:'backwards'}));
      });
      let remaining = animations.length;
      for (const animation of animations) animation.onfinish = () => { if (--remaining === 0) settle(el); };
      if (!remaining) settle(el);
    } catch (_) { settle(el); }
  }
  function enter(el, rect, delay = 0, forced = false) {
    if (seen.has(el) || playing.has(el)) return;
    unobserve(el);
    if (!enabled() || rect.bottom <= 0 || (!forced && rect.top < innerHeight * .2) || el.contains(document.activeElement)) { settle(el); return; }
    if (el.dataset.tmReveal !== 'body') { start(el, rect, delay, forced); return; }
    pending.add(el);
    prepareBody(el).then(targets => {
      if (!pending.has(el) || seen.has(el)) return;
      const current = el.getBoundingClientRect();
      // A first-screen entrance never follows someone who has already started reading.
      if (forced && !manual(el) && interacted) { settle(el); return; }
      start(el, current, delay, forced, targets || [el]);
    });
  }
  function createObservers() {
    observer?.disconnect(); bodyObserver?.disconnect(); prepareObserver?.disconnect();
    const entered = entries => {
      try {
        const batches = new Map();
        entries.filter(e => e.isIntersecting).sort((a,b) => a.boundingClientRect.top-b.boundingClientRect.top).forEach(entry => {
          const el = entry.target;
          if (!pending.has(el) || manual(el)) return;
          const group = el.closest('[data-tm-reveal-group]') || el;
          const count = batches.get(group) || 0; batches.set(group, count + 1);
          const stagger = milliseconds(getComputedStyle(el).getPropertyValue('--tm-reveal-stagger'), 80);
          enter(el, entry.boundingClientRect, Math.min(count, 2) * stagger);
        });
        entries.filter(e => !e.isIntersecting && e.boundingClientRect.bottom <= 0).forEach(e => settle(e.target));
      } catch (_) { settleAll(); }
    };
    observer = new IntersectionObserver(entered, {rootMargin:'0px 0px -' + Math.round(innerHeight * .12) + 'px 0px',threshold:0});
    bodyObserver = new IntersectionObserver(entered, {rootMargin:'0px 0px -' + Math.round(innerHeight * .06) + 'px 0px',threshold:0});
    prepareObserver = new IntersectionObserver(entries => {
      entries.filter(e => e.isIntersecting && pending.has(e.target) && !manual(e.target)).forEach(e => {
        prepareObserver.unobserve(e.target); prepareBody(e.target);
      });
    }, {rootMargin:'400px 0px',threshold:0});
    for (const el of pending) observe(el);
  }
  function observe(el) {
    if (manual(el)) return;
    (el.dataset.tmReveal === 'body' ? bodyObserver : observer)?.observe(el);
    if (el.dataset.tmReveal === 'body') prepareObserver?.observe(el);
  }
  function init(root = document) {
    if (!enabled()) return;
    try {
      if (!observer) createObservers();
      const items = discover(root);
      const readings = items.filter(el => !seen.has(el) && !pending.has(el) && !playing.has(el) && !manual(el)).map(el => ({
        el, rect:el.getBoundingClientRect(), transform:getComputedStyle(el).transform
      }));
      for (const {el,rect,transform} of readings) {
        if (rect.height === 0 || transform !== 'none' || el.closest('dialog,details:not([open])') ||
            el.parentElement?.closest('[aria-hidden="true"]') ||
            (el.getAttribute('aria-hidden') === 'true' && el.dataset.tmReveal !== 'media') ||
            el.contains(document.activeElement)) { seen.add(el); continue; }
        if (rect.top < innerHeight) {
          if (initial && fresh && !interacted && el.dataset.tmReveal !== 'media' && rect.bottom > 0) enter(el, rect, 0, true);
          else seen.add(el);
          continue;
        }
        pending.add(el); el.dataset.tmRevealState = 'pending'; observe(el);
      }
    } catch (_) { settleAll(); }
  }
  function reveal(root) {
    if (!root || !enabled()) return;
    try {
      discover(root).filter(el => textKind(el) && !seen.has(el)).forEach((el, index) => {
        enter(el, el.getBoundingClientRect(), Math.min(index, 2) * 80, true);
      });
    } catch (_) { settleAll(); }
  }
  function visibleNow() {
    const visible = new Set([...pending, ...playing.keys(), ...preparing.keys(), ...splits.keys()]);
    [...visible].filter(el => el.getBoundingClientRect().top < innerHeight).forEach(settle);
  }
  function unload(event) {
    new Set([...pending, ...playing.keys(), ...preparing.keys(), ...splits.keys()]).forEach(el => { if (event.target.contains(el)) settle(el); });
  }
  function load(event) { init(event.target); }
  function focus(event) {
    new Set([...pending, ...playing.keys(), ...preparing.keys()]).forEach(el => { if (el.contains(event.target)) settle(el); });
  }
  function preferenceChanged() { if (!enabled()) settleAll(); else init(); }
  function resized() {
    if (innerWidth === lastWidth) return;
    lastWidth = innerWidth;
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      visibleNow();
      for (const el of [...splits.keys()]) if (pending.has(el)) revertSplit(el);
      if (enabled() && pending.size) createObservers();
    });
  }
  function scrolled() {
    const now = performance.now(), distance = Math.abs(scrollY-lastScrollY);
    const jump = distance > innerHeight*.6, fast = distance/Math.max(1,now-lastScrollTime)>1.25;
    lastScrollY = scrollY; lastScrollTime = now;
    if ((!jump && !fast) || (!pending.size && !playing.size)) return;
    cancelAnimationFrame(jumpFrame);
    jumpFrame = requestAnimationFrame(() => {
      const readingLine = innerHeight*(fast ? .55 : .2);
      new Set([...pending,...playing.keys()]).forEach(el => { if (el.getBoundingClientRect().top < readingLine) settle(el); });
    });
  }
  function input(event) {
    interacted = true;
    new Set([...playing.keys(), ...preparing.keys()]).forEach(el => { if (el.contains(event.target)) settle(el); });
  }
  function restored(event) {
    if (event.type === 'pageshow' && !event.persisted && navigation !== 'back_forward') return;
    visibleNow(); init();
  }
  function visibility() { if (document.visibilityState === 'visible') visibleNow(); }
  function destroy() {
    destroyed = true; settleAll();
    observer?.disconnect(); bodyObserver?.disconnect(); prepareObserver?.disconnect();
    timers.forEach(clearTimeout); timers.clear();
    cancelAnimationFrame(resizeFrame); cancelAnimationFrame(jumpFrame);
    for (const [target,name,fn] of listeners) target.removeEventListener(name,fn);
    delete window.TMContentReveal;
  }
  const listeners = [
    [document,'shopify:section:load',load], [document,'shopify:section:unload',unload],
    [document,'shopify:block:select',focus], [document,'shopify:section:select',focus],
    [document,'focusin',focus], [document,'visibilitychange',visibility],
    [window,'hashchange',restored], [window,'pageshow',restored], [window,'resize',resized],
    [window,'scroll',scrolled], [window,'beforeprint',settleAll],
    [reduced,'change',preferenceChanged]
  ];
  if (connection?.addEventListener) listeners.push([connection,'change',preferenceChanged]);
  for (const name of ['wheel','touchstart','pointerdown','keydown']) listeners.push([window,name,input]);
  for (const [target,name,fn] of listeners) target.addEventListener(name,fn,{passive:true});
  window.TMContentReveal = {init,reveal,destroy};
  init(); initial = false;
})();
